import { ErrorNotice } from "../ui/ErrorNotice.js";
/**
 * Reference-image picker for a single option choice. Presigns via
 * `client.adminOptionReferenceUpload({ ext, group_id, value })` and PUTs the bytes straight
 * to R2/CDN (pixels never hit the API — Constitution III). A failed upload now shows an
 * inline error INSTEAD of the old silent data-URL fallback: these choices are platform-
 * global (served to every customer), and a multi-MB base64 blob silently persisted into a
 * short DB column both hides the broken hosting and breaks the record downstream — the
 * cloud rejects non-http(s) refs now (422). Extracted from OptionsPanel so ChoiceRow and
 * AddChoiceForm can both use it.
 */
import { useState } from "react";
import { useServices } from "../app/context.js";
import { isDurableRef } from "../app/refs.js";
import { MIME_EXT } from "./labels.js";
import { fetchWithTimeout, UPLOAD_TIMEOUT_MS } from "../templates/timeout.js";

export function ChoiceReferenceImage({
  value,
  onChange,
  testid,
  groupId,
  choiceValue,
}: {
  value?: string | null;
  onChange: (ref: string | null) => void;
  testid: string;
  groupId: string;
  choiceValue: string;
}) {
  const { client, io, logger } = useServices();
  const [busy, setBusy] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  async function pick() {
    const files = await io.pickFiles({
      accept: ["image/png", "image/jpeg", "image/webp"],
      multiple: false,
      maxBytes: 4 * 1024 * 1024,
    });
    if (!files.length) return;
    const f = files[0];
    const mime = f.mime ?? "image/png";
    const ext = MIME_EXT[mime] ?? "png";
    setBusy(true);
    setUploadError(null);
    try {
      // Presigned R2/CDN upload — store the public CDN URL (cached & shareable).
      const target = await client.adminOptionReferenceUpload({ ext, group_id: groupId, value: choiceValue });
      if (!/^https?:\/\//.test(target.upload_url)) throw new Error("hosting is local-only (no CDN target)");
      const res = await fetchWithTimeout(target.upload_url, {
        method: "PUT",
        headers: { "Content-Type": mime, ...(target.fields ?? {}) },
        body: f.bytes as BlobPart as BodyInit,
      }, UPLOAD_TIMEOUT_MS, "Reference image upload");
      if (!res.ok) throw new Error(`upload PUT -> ${res.status}`);
      if (!isDurableRef(target.public_url)) throw new Error("hosting returned a non-durable public URL — check hosting_public_host");
      onChange(target.public_url);
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      logger?.log("error", `Options · reference-image upload failed (${msg}) — the choice keeps its previous reference`);
      setUploadError(`Upload failed: ${msg}`);
    } finally {
      setBusy(false);
    }
  }

  return (
    <span className="ref-img">
      {value ? (
        <img
          src={value}
          alt="reference"
          className="ref-img__thumb choice-ref__thumb"
          data-testid={`${testid}-preview`}
        />
      ) : (
        <span className="faint">no ref</span>
      )}
      <button
        type="button"
        className="btn btn--sm btn--ghost"
        disabled={busy}
        data-testid={`${testid}-pick`}
        onClick={() => void pick()}
      >
        {busy ? "…" : "Image"}
      </button>
      {value && (
        <button
          type="button"
          className="btn btn--sm btn--ghost"
          data-testid={`${testid}-clear`}
          onClick={() => onChange(null)}
        >
          ×
        </button>
      )}
      {uploadError && (
        <span className="error" role="alert" data-testid={`${testid}-error`}><ErrorNotice detail={uploadError} /></span>
      )}
    </span>
  );
}
